import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { SectionHeader } from '../SectionHeader';
import { GlassCard } from '../GlassCard';
import { PaymentMethodCard } from './PaymentMethodCard';
import type { CardBrand } from './PaymentMethodCard';

export interface PaymentMethod {
  id: string;
  brand: CardBrand;
  label: string;
  lastFour: string;
  isDefault?: boolean;
  isExpired?: boolean;
  sourceId?: string | number;
  type?: 'card' | 'ach';
}

interface PaymentMethodsSectionProps {
  methods: PaymentMethod[];
  onManage?: () => void;
  onAddNew: () => void;
  gridLayout?: boolean;
  interactive?: boolean;
  onSetAsDefault?: (method: PaymentMethod) => void;
  onDelete?: (method: PaymentMethod) => void;
  isUpdating?: boolean;
  showEmptyState?: boolean;
  emptyStateIcon?: React.ReactNode;
  emptyStateTitle?: string;
  emptyStateMessage?: string;
  sectionTitle?: string;
  style?: StyleProp<ViewStyle>;
}

const PlusIcon = RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS;
const CreditCardIcon = RESIDENTS_SVG_COMPONENTS.CREDIT_CARD;

export const PaymentMethodsSection: React.FC<PaymentMethodsSectionProps> = React.memo(({
  methods,
  onManage,
  onAddNew,
  gridLayout = false,
  interactive = false,
  onSetAsDefault,
  onDelete,
  isUpdating = false,
  showEmptyState = false,
  emptyStateIcon,
  emptyStateTitle = 'No saved payment methods',
  emptyStateMessage = 'Add a credit card or bank account to make payments and set up autopay.',
  sectionTitle = 'Payment Methods',
  style,
}) => {
  const { colors, semantic } = useThemeColors();

  const isEmpty = methods.length === 0;
  const actionLabel = onManage ? 'Manage' : undefined;

  const displayTitle = interactive && methods.length > 0
    ? `${sectionTitle} (${methods.length})`
    : sectionTitle;

  const renderAddNewCard = () => (
    <TouchableOpacity
      style={[
        styles.addNewCard,
        { backgroundColor: colors.card.bg, borderColor: 'rgba(70,114,254,0.15)' },
        gridLayout && styles.gridItem,
        interactive && styles.addNewCardInteractive,
      ]}
      onPress={onAddNew}
      activeOpacity={0.7}
    >
      <View style={styles.addNewIconWell}>
        {PlusIcon && <PlusIcon width={16} height={16} color={semantic.interactive.primary} />}
      </View>
      <Text style={[styles.addNewText, { color: semantic.interactive.primary }]}>Add new</Text>
    </TouchableOpacity>
  );

  const renderCard = (method: PaymentMethod) => (
    <PaymentMethodCard
      key={method.id}
      brand={method.brand}
      label={method.label}
      lastFour={method.lastFour}
      isDefault={method.isDefault}
      isExpired={method.isExpired}
      interactive={interactive}
      onSetAsDefault={onSetAsDefault ? () => onSetAsDefault(method) : undefined}
      onDelete={onDelete ? () => onDelete(method) : undefined}
      isUpdating={isUpdating}
      canDelete={!method.isDefault}
      style={gridLayout ? styles.gridItem : undefined}
    />
  );

  if (isEmpty && showEmptyState) {
    return (
      <View style={style}>
        <SectionHeader title={displayTitle} actionLabel={actionLabel} onAction={onManage} />
        <GlassCard style={styles.emptyCard}>
          <View style={styles.emptyIconOuter}>
            <View style={styles.emptyIconGlow} />
            <View style={styles.emptyIconContainer}>
              {emptyStateIcon || (CreditCardIcon && (
                <CreditCardIcon width={28} height={28} color={colors.text.dimmer} />
              ))}
            </View>
          </View>
          <Text style={[styles.emptyTitle, { color: colors.text.primary }]}>{emptyStateTitle}</Text>
          <Text style={[styles.emptyMessage, { color: colors.text.muted }]}>{emptyStateMessage}</Text>
          <TouchableOpacity style={[styles.emptyAddButton, { backgroundColor: semantic.interactive.primary }]} onPress={onAddNew} activeOpacity={0.8}>
            <Text style={styles.emptyAddButtonText}>Add Payment Method</Text>
          </TouchableOpacity>
        </GlassCard>
      </View>
    );
  }

  if (isEmpty) return null;

  if (gridLayout) {
    return (
      <View style={style}>
        <SectionHeader title={displayTitle} actionLabel={actionLabel} onAction={onManage} />
        <GlassCard padding={16} borderRadius={16}>
          <View style={styles.gridContainer}>
            {methods.map(renderCard)}
            {renderAddNewCard()}
          </View>
        </GlassCard>
      </View>
    );
  }

  return (
    <View style={style}>
      <SectionHeader title={displayTitle} actionLabel={actionLabel} onAction={onManage} />
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {methods.map(renderCard)}
        {renderAddNewCard()}
      </ScrollView>
    </View>
  );
});

PaymentMethodsSection.displayName = 'PaymentMethodsSection';

const GRID_GAP = 12;

const styles = StyleSheet.create({
  scrollContent: {
    gap: GRID_GAP,
    paddingRight: 24,
  },
  gridContainer: {
    gap: GRID_GAP,
  },
  gridItem: {
    width: '100%',
  },
  addNewCard: {
    width: 160,
    minHeight: 110,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 16,
  },
  addNewCardInteractive: {
    width: '100%',
  },
  addNewIconWell: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(70,114,254,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(70,114,254,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addNewText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  emptyCard: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyIconOuter: {
    width: 64,
    height: 64,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyIconGlow: {
    position: 'absolute',
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(70,114,254,0.06)',
  },
  emptyIconContainer: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyMessage: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
    maxWidth: 280,
  },
  emptyAddButton: {
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingHorizontal: 32,
    height: REDESIGN_SPACING.buttonHeight,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  emptyAddButtonText: {
    ...REDESIGN_TYPOGRAPHY.button,
    color: '#FFFFFF',
  },
});
